/**
 * /admin/copys — Gerador de copys para Meta Ads.
 *
 * Seleciona a página do produto (certificação ou pós), mostra a oferta atual e
 * gera 5 legendas + 1 título curto + 1 descrição curta com a Lovable AI.
 * Não salva nada: a geração é sempre na hora.
 */
import { createFileRoute, Link } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useMutation } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import { toast } from "sonner";
import { ArrowLeft, Check, Copy, ExternalLink, Loader2, Sparkles } from "lucide-react";

import { AdminGate } from "@/components/admin/AdminGate";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

import { COPY_LIMITS, COPY_PRODUCTS, anglesFor, findCopyProduct } from "@/lib/copys/catalog";
import { generateAdCopy, type AdCopyPackage } from "@/lib/copys/admin.functions";

export const Route = createFileRoute("/admin/copys")({
  head: () => ({
    meta: [
      { title: "Admin — Copys Meta Ads" },
      { name: "robots", content: "noindex,nofollow" },
    ],
  }),
  component: () => (
    <AdminGate route="/admin/copys">
      <CopysPage />
    </AdminGate>
  ),
});

function CopyButton({ text, label }: { text: string; label?: string }) {
  const [done, setDone] = useState(false);
  return (
    <Button
      type="button"
      size="sm"
      variant="outline"
      onClick={async () => {
        try {
          await navigator.clipboard.writeText(text);
          setDone(true);
          toast.success("Copiado");
          setTimeout(() => setDone(false), 1500);
        } catch {
          toast.error("Não foi possível copiar");
        }
      }}
    >
      {done ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
      {label ? <span className="ml-1.5">{label}</span> : null}
    </Button>
  );
}

function CharCount({ value, limit, warn }: { value: string; limit: number; warn?: number }) {
  const over = value.length > limit;
  const warning = !over && warn !== undefined && value.length > warn;
  const cls = over
    ? "text-destructive text-xs font-medium"
    : warning
      ? "text-xs font-medium text-amber-600 dark:text-amber-500"
      : "text-muted-foreground text-xs";
  return (
    <span className={cls} title={warning ? "O Meta corta com 'ver mais' após 125 caracteres" : undefined}>
      {value.length}/{limit}
    </span>
  );
}


function CopysPage() {
  const generate = useServerFn(generateAdCopy);
  const [productId, setProductId] = useState<string>(COPY_PRODUCTS[0]?.id ?? "");
  const [observacoes, setObservacoes] = useState("");
  const [pkg, setPkg] = useState<AdCopyPackage | null>(null);

  const product = useMemo(() => findCopyProduct(productId), [productId]);
  const grupos = useMemo(() => {
    const map = new Map<string, typeof COPY_PRODUCTS>();
    for (const p of COPY_PRODUCTS) {
      const list = map.get(p.grupoLabel) ?? [];
      list.push(p);
      map.set(p.grupoLabel, list);
    }
    return [...map.entries()];
  }, []);

  const mutation = useMutation({
    mutationFn: () =>
      generate({ data: { productId, observacoes: observacoes.trim() || undefined } }),
    onSuccess: (result) => {
      setPkg(result);
      toast.success("Copys geradas");
    },
    onError: (error: unknown) => {
      toast.error(error instanceof Error ? error.message : "Falha ao gerar as copys");
    },
  });

  const allText = pkg
    ? [
        ...pkg.legendas.map((l, i) => `TEXTO PRINCIPAL ${i + 1} — ${l.angulo}\n${l.texto}`),
        `TÍTULOS:\n${pkg.titulos.map((t) => `- ${t}`).join("\n")}`,
        `DESCRIÇÕES:\n${pkg.descricoes.map((d) => `- ${d}`).join("\n")}`,
      ].join("\n\n")
    : "";


  return (
    <div className="min-h-screen">
      <div className="mx-auto max-w-4xl px-4 py-8 sm:px-6">
        <Link
          to="/admin"
          className="text-muted-foreground hover:text-foreground mb-6 inline-flex items-center gap-2 text-sm"
        >
          <ArrowLeft className="h-4 w-4" /> Voltar ao admin
        </Link>

        <header className="mb-8">
          <h1 className="text-2xl font-bold tracking-tight sm:text-3xl">Copys para Meta Ads</h1>
          <p className="text-muted-foreground mt-2 text-sm">
            Escolha a página do produto e gere 5 opções de texto principal, 3 títulos e 3 descrições,
            já no formato do Meta Ads e alinhadas com a oferta que está no ar.
          </p>

        </header>

        <section className="bg-card rounded-xl border p-5">
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-2">
              <Label>Página do produto</Label>
              <Select value={productId} onValueChange={(v) => setProductId(v)}>
                <SelectTrigger>
                  <SelectValue placeholder="Selecione" />
                </SelectTrigger>
                <SelectContent>
                  {grupos.map(([label, items]) => (
                    <SelectGroup key={label}>
                      <SelectLabel>{label}</SelectLabel>
                      {items.map((p) => (
                        <SelectItem key={p.id} value={p.id}>
                          {p.nome}
                        </SelectItem>
                      ))}
                    </SelectGroup>
                  ))}
                </SelectContent>
              </Select>
              {product ? (
                <a
                  href={product.path}
                  target="_blank"
                  rel="noreferrer"
                  className="text-muted-foreground hover:text-foreground inline-flex items-center gap-1 text-xs"
                >
                  ver {product.path} <ExternalLink className="h-3 w-3" />
                </a>
              ) : null}
            </div>

            <div className="space-y-2">
              <Label htmlFor="obs">Observações (opcional)</Label>
              <Textarea
                id="obs"
                rows={4}
                value={observacoes}
                onChange={(e) => setObservacoes(e.target.value)}
                placeholder="Ex: focar em recém-formados, sem emoji, destacar o certificado"
              />
            </div>
          </div>

          {product ? (
            <div className="bg-muted/40 mt-5 rounded-lg border p-4">
              <p className="text-xs font-semibold tracking-wide uppercase">
                Oferta atual lida da página
                <span className="text-muted-foreground ml-2 font-normal normal-case">
                  {product.ofertaTipo === "gratuito"
                    ? "captura gratuita"
                    : product.ofertaTipo === "lowticket"
                      ? "captura gratuita + oferta low-ticket depois"
                      : "produto pago"}
                </span>
              </p>
              <ul className="text-muted-foreground mt-2 space-y-1 text-sm">
                {product.oferta.map((line) => (
                  <li key={line} className="break-words">
                    • {line}
                  </li>
                ))}
              </ul>
              <p className="text-muted-foreground mt-3 text-xs">
                Lido agora ({new Date().toLocaleString("pt-BR")}) direto da configuração da página. Se
                algo aqui estiver errado, o ajuste é na página — a copy acompanha automaticamente.
              </p>
              <p className="text-muted-foreground mt-2 text-xs">
                Ângulos das 5 opções: {anglesFor(product.grupo).join(" · ")}
              </p>
            </div>
          ) : null}

          <div className="mt-5 flex flex-wrap items-center gap-3">
            <Button onClick={() => mutation.mutate()} disabled={!product || mutation.isPending}>
              {mutation.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Sparkles className="h-4 w-4" />
              )}
              <span className="ml-2">{pkg ? "Gerar novamente" : "Gerar copys"}</span>
            </Button>
            {pkg ? <CopyButton text={allText} label="Copiar tudo" /> : null}
            {mutation.isPending ? (
              <span className="text-muted-foreground text-xs">
                A IA está lendo a oferta da página...
              </span>
            ) : null}
          </div>
        </section>

        {pkg ? (
          <section className="mt-8 space-y-4">
            <div className="flex items-baseline justify-between gap-3">
              <h2 className="text-lg font-semibold">Texto principal</h2>
              <span className="text-muted-foreground text-xs">
                o Meta corta com "ver mais" após {COPY_LIMITS.legendaVisivel} caracteres
              </span>
            </div>
            {pkg.legendas.map((l, i) => (
              <div key={i} className="bg-card rounded-xl border p-4">
                <div className="mb-2 flex items-center justify-between gap-3">
                  <span className="text-xs font-semibold tracking-wide uppercase">
                    Opção {i + 1}
                    <span className="text-muted-foreground ml-2 font-normal normal-case">
                      {l.angulo}
                    </span>
                  </span>
                  <div className="flex items-center gap-2">
                    <CharCount
                      value={l.texto}
                      limit={COPY_LIMITS.legenda}
                      warn={COPY_LIMITS.legendaVisivel}
                    />
                    <CopyButton text={l.texto} />
                  </div>
                </div>
                <p className="text-sm leading-relaxed whitespace-pre-line">{l.texto}</p>
              </div>
            ))}

            <div className="grid gap-4 sm:grid-cols-2">
              <div className="bg-card rounded-xl border p-4">
                <h3 className="mb-3 text-xs font-semibold tracking-wide uppercase">Título</h3>
                <ul className="space-y-3">
                  {pkg.titulos.map((t, i) => (
                    <li key={i} className="flex items-start justify-between gap-3">
                      <p className="text-sm font-medium">{t}</p>
                      <div className="flex shrink-0 items-center gap-2">
                        <CharCount value={t} limit={COPY_LIMITS.titulo} />
                        <CopyButton text={t} />
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
              <div className="bg-card rounded-xl border p-4">
                <h3 className="mb-3 text-xs font-semibold tracking-wide uppercase">Descrição</h3>
                <ul className="space-y-3">
                  {pkg.descricoes.map((d, i) => (
                    <li key={i} className="flex items-start justify-between gap-3">
                      <p className="text-sm font-medium">{d}</p>
                      <div className="flex shrink-0 items-center gap-2">
                        <CharCount value={d} limit={COPY_LIMITS.descricao} />
                        <CopyButton text={d} />
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </section>
        ) : null}

      </div>
    </div>
  );
}
